import React, { useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { StepYou } from './StepYou';
import { StepSeat } from './StepSeat';
import { StepWork } from './StepWork';
import { StepQuestions } from './StepQuestions';
import { EMPTY_APPLICATION, type ApplicationForm, type FieldErrors } from '../../types/careers';
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/mmuChs2XqH3KvF4a7fk2/webhook-trigger/laO0oSsIulBjUpiOERDf';
const UPLOADCARE_PUB_KEY = 'fff7c1111ac2fc5b8306';
const STEP_TITLES = ['You', 'The seat', 'Your work', 'Written questions'];
const MIN_ANSWER_LENGTH = 30;

/** Accepts a handle or a full link; always stores a full Instagram URL. */
function normalizeInstagram(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const handle = raw.replace(/^@/, '').replace(/^instagram\.com\//i, '');
  return `https://instagram.com/${handle}`;
}
async function uploadToUploadcare(file: File): Promise<string> {
  const body = new FormData();
  body.append('UPLOADCARE_PUB_KEY', UPLOADCARE_PUB_KEY);
  body.append('UPLOADCARE_STORE', '1');
  body.append('file', file);
  const response = await fetch('https://upload.uploadcare.com/base/', {
    method: 'POST',
    body
  });
  if (!response.ok) throw new Error('Upload failed');
  const result = await response.json();
  return `https://ucarecdn.com/${result.file}/`;
}
function validateStep(step: number, form: ApplicationForm, resumeFile: File | null): FieldErrors {
  const errors: FieldErrors = {};
  if (step === 0) {
    if (!form.firstName.trim()) errors.firstName = 'Required.';
    if (!form.lastName.trim()) errors.lastName = 'Required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
    if (form.phone.replace(/\D/g, '').length < 10) {
      errors.phone = 'Enter a valid phone number with area code.';
    }
  }
  if (step === 1) {
    if (!form.primarySeat) errors.primarySeat = 'Pick your main seat.';
    if (!form.location.trim()) errors.location = 'Required.';
    if (!form.timezone) errors.timezone = 'Required.';
    if (!form.workSetup) errors.workSetup = 'Required.';
    if (!form.earliestStart) errors.earliestStart = 'Required.';
  }
  if (step === 2) {
    if (!form.yearsExperience) errors.yearsExperience = 'Required.';
    if (!resumeFile) errors.resume = 'Attach your resume as a PDF.';
  }
  if (step === 3) {
    const answers = ['answerResult', 'answerDetail', 'answerPushback', 'answerThinking'] as const;
    answers.forEach(field => {
      if (form[field].trim().length < MIN_ANSWER_LENGTH) {
        errors[field] = 'Give us a bit more detail.';
      }
    });
    if (!form.compensation.trim()) errors.compensation = 'Required.';
    if (!form.howDidYouHear) errors.howDidYouHear = 'Required.';
  }
  return errors;
}
export function ApplicationWizard() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<ApplicationForm>(EMPTY_APPLICATION);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const topRef = useRef<HTMLDivElement>(null);
  const isLastStep = step === STEP_TITLES.length - 1;
  const handleChange = <K extends keyof ApplicationForm,>(field: K, value: ApplicationForm[K]) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };
  const handleResumeChange = (file: File | null) => {
    setResumeFile(file);
    setErrors(prev => ({
      ...prev,
      resume: undefined
    }));
  };
  const scrollToTop = () => {
    topRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const goBack = () => {
    setErrors({});
    setStep(prev => Math.max(0, prev - 1));
    scrollToTop();
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const stepErrors = validateStep(step, form, resumeFile);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      scrollToTop();
      return;
    }
    if (!isLastStep) {
      setErrors({});
      setStep(prev => prev + 1);
      scrollToTop();
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    const payload: Record<string, string> = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      okToText: form.okToText ? 'Yes' : 'No',
      primarySeat: form.primarySeat,
      otherSeats: form.otherSeats.join(', '),
      location: form.location.trim(),
      timezone: form.timezone,
      workSetup: form.workSetup,
      work_setup: form.workSetup,
      earliestStart: form.earliestStart,
      yearsExperience: form.yearsExperience,
      portfolioUrl: normalizeInstagram(form.portfolioUrl),
      answerResult: form.answerResult.trim(),
      answerDetail: form.answerDetail.trim(),
      answerPushback: form.answerPushback.trim(),
      answer_thinking: form.answerThinking.trim(),
      compensation: form.compensation.trim(),
      howDidYouHear: form.howDidYouHear,
      readThePage: /^machine\b/i.test(form.answerResult.trim()) ? 'Yes' : 'No'
    };
    if (resumeFile) {
      try {
        payload.resumeUrl = await uploadToUploadcare(resumeFile);
      } catch {
        payload.resumeUrl = 'Upload failed — request resume directly';
      }
    }
    try {
      const params = new URLSearchParams(payload);
      const beacon = new Image();
      beacon.src = `${WEBHOOK_URL}?${params.toString()}`;
      setIsSubmitting(false);
      setIsSuccess(true);
      scrollToTop();
    } catch {
      setIsSubmitting(false);
      setSubmitError('Something went wrong. Please try again.');
    }
  };
  if (isSuccess) {
    return <div ref={topRef} className="scroll-mt-28" data-id="element-30" data-mp="2pzlvlxlo6znx">
        <div className="bg-engage-gray1 border border-engage-gray2 rounded-lg p-8 md:p-16 text-center" data-id="element-31" data-mp="2tzvy30a977y1">
          <div className="w-16 h-16 bg-engage-yellow/10 border border-engage-yellow/30 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-32" data-mp="2g0d4dci6vz59">
            <svg className="w-8 h-8 text-engage-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true" data-id="element-33" data-mp="2kxmpwh2gjqnt">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-34" data-mp="1x9lpjvffngjh" />
            </svg>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-engage-white mb-4" data-id="element-35" data-mp="226lq5qtijr37">
            Application in
          </h2>
          <p className="text-engage-gray4 text-lg max-w-xl mx-auto mb-10" data-id="element-36" data-mp="1sa91x8gqm5fu">
            You'll hear from us within 3 business days, yes or no. If it's a
            yes, the next email has your video prompts.
          </p>
          <Button variant="secondary" onClick={() => {
          setForm(EMPTY_APPLICATION);
          setResumeFile(null);
          setStep(0);
          setIsSuccess(false);
        }} data-id="element-37" data-mp="14nx1sn1kyqhg">
            Submit another application
          </Button>
        </div>
      </div>;
  }
  return <div ref={topRef} className="scroll-mt-28" data-id="element-38" data-mp="1rlrscul3ttb4">
      <div className="bg-engage-gray1 border border-engage-gray2 rounded-lg p-6 sm:p-8 md:p-12" data-id="element-39" data-mp="1nmizdrxw1zgm">
        {/* Progress */}
        <div className="mb-10" data-id="element-40" data-mp="21c306s7wsmna">
          <div className="flex gap-1.5" role="presentation" data-id="element-41" data-mp="21fiehstrdrnn">
            {STEP_TITLES.map((title, index) => <span key={title} className={`h-1 flex-1 rounded-full transition-colors duration-200 ease-out ${index <= step ? 'bg-engage-yellow' : 'bg-engage-gray2'}`} data-id="element-42" data-mp="2bblhf0wfpubn" data-mpi={index} />)}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-engage-white mt-6" data-id="element-43" data-mp="r297pbjg2iw2">
            {STEP_TITLES[step]}
          </h2>
        </div>

        <form onSubmit={handleSubmit} noValidate data-id="element-44" data-mp="10xt2tvq6pdjr">
          <div key={step} className="animate-step-in" data-id="element-45" data-mp="10z41jh4rvv40">
            {step === 0 && <StepYou form={form} errors={errors} onChange={handleChange} data-id="element-46" data-mp="15wxa2gtjt17g" />}
            {step === 1 && <StepSeat form={form} errors={errors} onChange={handleChange} data-id="element-47" data-mp="87khxw49kf1h" />}
            {step === 2 && <StepWork form={form} errors={errors} onChange={handleChange} resumeFile={resumeFile} onResumeChange={handleResumeChange} data-id="element-48" data-mp="i84lj5f99vmv" />}
            {step === 3 && <StepQuestions form={form} errors={errors} onChange={handleChange} data-id="element-49" data-mp="3lmh2bs2u9qox" />}
          </div>

          {submitError && <p className="mt-8 bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 text-red-400 text-sm" data-id="element-50" data-mp="27q7dxu3o8zve">
              {submitError}
            </p>}

          <div className="mt-10 pt-8 border-t border-engage-gray2 flex flex-col-reverse sm:flex-row sm:items-center gap-4" data-id="element-51" data-mp="1yqahh6b1iwps">
            {step > 0 && <button type="button" onClick={goBack} className="text-engage-gray4 font-semibold px-2 py-3 rounded-sm hover:text-engage-white transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-engage-yellow" data-id="element-52" data-mp="1otfof2xjdoiw">
                Back
              </button>}
            <div className="sm:ml-auto" data-id="element-53" data-mp="1b0g84ypxjqaa">
              <Button type="submit" variant="primary" fullWidth className="text-lg py-4 sm:px-10" disabled={isSubmitting} data-id="element-54" data-mp="163g3ms8qavla">
                {isSubmitting ? 'Submitting…' : isLastStep ? 'Submit application' : 'Continue'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>;
}